if (!customElements.get('cozypod-pdp-video')) {
  customElements.define(
    'cozypod-pdp-video',
    class CozypodPdpVideo extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.modal = this.querySelector('[data-cpd-video-modal]');
        if (!this.modal) return;

        this.modal.addEventListener(
          'modal:afterShow',
          () => requestAnimationFrame(() => this.getActiveVideoMedia()?.play()),
          { signal: this.abortController.signal }
        );
        this.modal.addEventListener(
          'modal:afterHide',
          () => this.modal.querySelectorAll('video-media').forEach((videoMedia) => videoMedia.pause()),
          { signal: this.abortController.signal }
        );
      }

      getActiveVideoMedia() {
        const mobileVideo = this.modal.querySelector('.mobile\\:deferred-media');
        const desktopVideo = this.modal.querySelector('video-media:not(.mobile\\:deferred-media)');
        const prefersMobile = window.matchMedia('(max-width: 767px)').matches;

        if (prefersMobile && mobileVideo) {
          return mobileVideo;
        }

        return desktopVideo || mobileVideo || this.modal.querySelector('video-media');
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }
    }
  );
}

if (!customElements.get('cozypod-safety-slider')) {
  customElements.define(
    'cozypod-safety-slider',
    class CozypodSafetySlider extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.track = this.querySelector('[data-cpd-slider-track]');
        this.viewport = this.querySelector('[data-cpd-slider-viewport]');
        this.slides = [...this.querySelectorAll('[data-cpd-slider-slide]')];
        this.prevButton = this.querySelector('[data-cpd-slider-prev]');
        this.nextButton = this.querySelector('[data-cpd-slider-next]');

        if (!this.track || this.slides.length === 0) return;

        this.index = 0;
        this.autoplaySpeed = (parseInt(this.getAttribute('autoplay-speed'), 10) || 5) * 1000;
        this.autoplayEnabled =
          this.hasAttribute('autoplay') &&
          this.slides.length > 1 &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayTimer = null;

        this.syncViewportHeight = () => {
          if (!this.viewport) return;

          const activeSlide = this.slides[this.index];
          if (!activeSlide) return;

          const image = activeSlide.querySelector('.cpd-safety-slider__image');
          if (!image) {
            this.viewport.style.height = '';
            return;
          }

          const applyHeight = () => {
            this.viewport.style.height = `${activeSlide.offsetHeight}px`;
          };

          if (image.tagName !== 'IMG' || image.complete) {
            applyHeight();
          } else {
            image.addEventListener('load', applyHeight, { once: true });
          }
        };

        this.update = () => {
          this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;

          if (this.prevButton) {
            this.prevButton.disabled = false;
          }

          if (this.nextButton) {
            this.nextButton.disabled = false;
          }

          this.syncViewportHeight();
        };

        this.goTo = (nextIndex) => {
          const total = this.slides.length;
          if (total < 2) return;

          this.index = ((nextIndex % total) + total) % total;
          this.update();
        };

        this.stopAutoplay = () => {
          if (this.autoplayTimer) {
            window.clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
          }
        };

        this.startAutoplay = () => {
          this.stopAutoplay();

          if (!this.autoplayEnabled) return;

          this.autoplayTimer = window.setInterval(() => {
            this.goTo(this.index + 1);
          }, this.autoplaySpeed);
        };

        this.resetAutoplay = () => {
          this.stopAutoplay();
          this.startAutoplay();
        };

        this.prevButton?.addEventListener('click', () => {
          this.goTo(this.index - 1);
          this.resetAutoplay();
        }, { signal });

        this.nextButton?.addEventListener('click', () => {
          this.goTo(this.index + 1);
          this.resetAutoplay();
        }, { signal });

        let touchStartX = 0;
        let touchDeltaX = 0;

        this.track.addEventListener('touchstart', (event) => {
          touchStartX = event.touches[0].clientX;
          touchDeltaX = 0;
        }, { passive: true, signal });

        this.track.addEventListener('touchmove', (event) => {
          touchDeltaX = event.touches[0].clientX - touchStartX;
        }, { passive: true, signal });

        this.track.addEventListener('touchend', () => {
          if (Math.abs(touchDeltaX) < 40) return;

          if (touchDeltaX < 0) {
            this.goTo(this.index + 1);
          } else if (touchDeltaX > 0) {
            this.goTo(this.index - 1);
          }

          this.resetAutoplay();
        }, { signal });

        this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal });
        this.addEventListener('mouseleave', () => this.startAutoplay(), { signal });
        this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
        this.addEventListener('focusout', () => this.startAutoplay(), { signal });

        window.addEventListener('resize', () => this.syncViewportHeight(), { signal });

        this.slides.forEach((slide) => {
          const media = slide.querySelector('.cpd-safety-slider__image');
          if (media?.tagName === 'IMG' && !media.complete) {
            media.addEventListener(
              'load',
              () => {
                if (slide === this.slides[this.index]) {
                  this.syncViewportHeight();
                }
              },
              { once: true }
            );
          }
        });

        this.update();
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.abortController?.abort();
      }
    }
  );
}

if (!customElements.get('cozypod-remote-hotspots')) {
  customElements.define(
    'cozypod-remote-hotspots',
    class CozypodRemoteHotspots extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.hotspots = [...this.querySelectorAll('.cpd-remote__hotspot')];
        this.tooltipLayer = this.querySelector('.cpd-remote__tooltip-layer');
        this.activeHotspot = null;
        this.activeTooltip = null;

        this.hotspots.forEach((hotspot) => {
          hotspot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (hotspot.classList.contains('is-active')) {
              this.closeAll();
              return;
            }

            this.openHotspot(hotspot);
          }, { signal });
        });

        this.addEventListener('click', (event) => {
          if (!event.target.closest('.cpd-remote__hotspot') && this.activeHotspot) {
            this.closeAll();
          }
        }, { signal });

        this.repositionActiveTooltip = () => {
          if (this.activeHotspot && this.activeTooltip) {
            this.positionTooltip(this.activeHotspot, this.activeTooltip);
          }
        };

        window.addEventListener('resize', this.repositionActiveTooltip, { signal });
        window.addEventListener('scroll', this.repositionActiveTooltip, { passive: true, signal });

        document.addEventListener('click', (event) => {
          if (this.contains(event.target)) return;
          if (this.activeTooltip?.contains(event.target)) return;
          this.closeAll();
        }, { signal });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            this.closeAll();
          }
        }, { signal });
      }

      mountTooltip(tooltip) {
        if (tooltip.parentElement !== document.body) {
          document.body.appendChild(tooltip);
        }
      }

      restoreTooltip(tooltip) {
        if (!this.tooltipLayer || tooltip.parentElement === this.tooltipLayer) return;
        this.tooltipLayer.appendChild(tooltip);
      }

      getTooltip(hotspot) {
        const tooltipId = hotspot.getAttribute('aria-controls');
        return tooltipId ? document.getElementById(tooltipId) : null;
      }

      openHotspot(hotspot) {
        const tooltip = this.getTooltip(hotspot);
        if (!tooltip) return;

        this.closeAll();

        hotspot.classList.add('is-active');
        hotspot.setAttribute('aria-expanded', 'true');
        tooltip.hidden = false;
        tooltip.classList.add('is-visible');

        this.activeHotspot = hotspot;
        this.activeTooltip = tooltip;
        this.classList.add('has-active-hotspot');
        this.mountTooltip(tooltip);

        this.positionTooltip(hotspot, tooltip);

        requestAnimationFrame(() => {
          if (this.activeHotspot === hotspot && this.activeTooltip === tooltip) {
            this.positionTooltip(hotspot, tooltip);
          }
        });
      }

      positionTooltip(hotspot, tooltip) {
        const hotspotRect = hotspot.getBoundingClientRect();
        const viewportPadding = 12;
        const gap = 14;
        const vertical = parseFloat(hotspot.style.getPropertyValue('--hotspot-y')) || 50;

        tooltip.style.left = `${hotspotRect.left + hotspotRect.width / 2}px`;
        tooltip.style.top = `${hotspotRect.bottom + gap}px`;
        tooltip.style.transform = 'translateX(-50%)';

        const tooltipRect = tooltip.getBoundingClientRect();
        let centerX = hotspotRect.left + hotspotRect.width / 2;
        let top = hotspotRect.bottom + gap;
        const transform = 'translateX(-50%)';

        const halfWidth = Math.max(tooltipRect.width / 2, 80);
        const minCenter = viewportPadding + halfWidth;
        const maxCenter = window.innerWidth - viewportPadding - halfWidth;
        centerX = Math.min(Math.max(centerX, minCenter), maxCenter);

        const preferAbove = vertical >= 68;
        const spaceBelow = window.innerHeight - viewportPadding - hotspotRect.bottom - gap;
        const spaceAbove = hotspotRect.top - gap - viewportPadding;

        if (preferAbove || top + tooltipRect.height > window.innerHeight - viewportPadding) {
          if (spaceAbove >= tooltipRect.height || spaceAbove > spaceBelow) {
            top = hotspotRect.top - gap - tooltipRect.height;
          }
        }

        if (top < viewportPadding) {
          top = viewportPadding;
        }

        tooltip.style.left = `${centerX}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.transform = transform;
      }

      closeAll() {
        this.hotspots.forEach((hotspot) => {
          hotspot.classList.remove('is-active');
          hotspot.setAttribute('aria-expanded', 'false');

          const tooltip = this.getTooltip(hotspot);
          if (!tooltip) return;

          tooltip.classList.remove('is-visible');
          tooltip.hidden = true;
          tooltip.style.left = '';
          tooltip.style.top = '';
          tooltip.style.transform = '';
          this.restoreTooltip(tooltip);
        });

        this.activeHotspot = null;
        this.activeTooltip = null;
        this.classList.remove('has-active-hotspot');
      }

      disconnectedCallback() {
        this.closeAll();
        this.abortController?.abort();
      }
    }
  );
}
